import React from "react";
import { createPortal } from "react-dom";
import { useShallow } from "zustand/react/shallow";
import Button from "../ui/Button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../ui/Collapsible";
import InfoTooltip from "../ui/InfoTooltip";
import FileUploadPanelMenu from "../draggable-panels/FileUploadPanelMenu";
import PoseEditor from "../draggable-panels/PoseEditor";
import { usePanelStore } from "../../stores/panelStore";
import { useSelectionStore } from "../../stores/selectionStore";
import { useFileStore } from "../../stores/fileStore";
import { useFocusTrap } from "../../hooks/useFocusTrap";
import {
  AlertTriangle,
  Download,
  Mail,
  MessageSquare,
  Package,
  ShieldCheck,
  X,
} from "lucide-react";
import { EXPORT_REVIEW_ACTION_BUTTON_CLASS, EXPORT_REVIEW_LABELS } from "./constants";

const FileUploadPanelContainer = React.memo(function FileUploadPanelContainer({
  open,
  onClose,
  titleIcon,
  handleFiles,
  mobilePinPanelId,
}) {
  const loadingPreset = usePanelStore((state) => state.loadingPreset);
  const selectedKeyframe = useSelectionStore((state) => state.selectedKeyframe);
  const { assetFolders, fileMap, missingFiles, keyframes, uploadingFiles } = useFileStore(
    useShallow((state) => ({
      assetFolders: state.assetFolders,
      fileMap: state.fileMap,
      missingFiles: state.missingFiles,
      keyframes: state.keyframes,
      uploadingFiles: state.uploadingFiles,
    }))
  );
  const fileActions = useFileStore.getState().actions;

  return (
    <FileUploadPanelMenu
      open={open}
      onClose={onClose}
      titleIcon={titleIcon}
      handleFiles={handleFiles}
      mobilePinPanelId={mobilePinPanelId}
      assetFolders={assetFolders}
      loadingPreset={loadingPreset}
      loadPresetFolder={fileActions.loadPresetFolder}
      fileMap={fileMap}
      missingFiles={missingFiles}
      uploadingFiles={uploadingFiles}
      keyframes={keyframes}
      handleKeyframeSelect={fileActions.handleKeyframeSelect}
      selectedKeyframe={selectedKeyframe}
    />
  );
});

const getCountValue = (counts, label) => counts?.find((item) => item.label === label)?.value;

const hasProblemCount = (counts) => {
  const missingAssets = Number(getCountValue(counts, "Missing assets") ?? 0);
  const warnings = Number(getCountValue(counts, "Warnings") ?? 0);
  const unmappedActuators = Number(getCountValue(counts, "Unmapped actuators") ?? 0);

  return missingAssets > 0 || warnings > 0 || unmappedActuators > 0;
};

const ExportModalDisclosure = React.memo(function ExportModalDisclosure({
  title,
  defaultOpen = false,
  children,
}) {
  const [open, setOpen] = React.useState(defaultOpen);

  React.useEffect(() => {
    if (defaultOpen) setOpen(true);
  }, [defaultOpen]);

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="export-modal-disclosure rounded-md">
      <CollapsibleTrigger className="rounded-md px-2 py-2 text-xs font-medium text-gray-200">
        {title}
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="rounded-md border border-gray-700/70 bg-black/20 p-2">{children}</div>
      </CollapsibleContent>
    </Collapsible>
  );
});

const ExportCountsDisclosure = React.memo(function ExportCountsDisclosure({ counts, defaultOpen }) {
  if (!counts?.length) return null;

  return (
    <ExportModalDisclosure title="Export details" defaultOpen={defaultOpen}>
      <div className="grid grid-cols-2 gap-2 text-xs text-gray-200">
        {counts.map((item) => (
          <div key={item.label}>
            {item.label}: {item.value}
          </div>
        ))}
      </div>
    </ExportModalDisclosure>
  );
});

const ExportReviewModal = React.memo(function ExportReviewModal({
  exportName,
  review,
  counts,
  helperText,
  extraContent,
  hideSummary = false,
  projectName = "",
  onProjectNameChange,
  primaryLabel = "Download",
  secondaryTitle,
  secondaryDescription,
  secondaryMeta,
  secondaryLabel,
  onSecondaryDownload,
  downloadComplete = false,
  onOpenFeedback,
  onOpenUpdates,
  onDownload,
  onCancel,
}) {
  const dialogRef = React.useRef(null);
  useFocusTrap(dialogRef, Boolean(review));
  if (!review) return null;
  const title = EXPORT_REVIEW_LABELS[review.status] || "Export";
  const isClean = review.status === "ready";
  const isBlocked = review.status === "blocked";
  const details = [...(review.blockers || []), ...(review.warnings || [])].slice(0, 8);
  const showCountsByDefault = details.length > 0 || hasProblemCount(counts);
  const actionLabel = isClean ? primaryLabel : `${primaryLabel} Anyway`;

  return createPortal(
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/60 p-4"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onCancel();
      }}
    >
      <div
        ref={dialogRef}
        className="relative w-full max-w-md rounded-lg border border-gray-700 p-4 text-gray-100 shadow-xl backdrop-blur-sm"
        style={{ backgroundColor: "var(--panel-surface)" }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="export-review-title"
        tabIndex={-1}
      >
        <button
          type="button"
          className="absolute right-3 top-3 rounded p-1 text-gray-400 transition-colors hover:text-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-500"
          onPointerDown={(event) => {
            event.stopPropagation();
            onCancel();
          }}
          onClick={onCancel}
          aria-label="Close export review"
        >
          <X className="h-4 w-4" aria-hidden />
        </button>
        {downloadComplete ? (
          <>
            <div className="mb-3 flex items-start gap-3">
              <div className="mt-0.5 rounded-md border border-emerald-400/80 bg-emerald-500/10 p-2 text-emerald-300 shadow-[0_0_0_1px_rgba(255,255,255,0.08)]">
                <ShieldCheck className="h-5 w-5" aria-hidden />
              </div>
              <div className="min-w-0">
                <h2 id="export-review-title" className="text-base font-semibold text-gray-50">
                  Download Successful
                </h2>
                <p className="mt-1 text-sm text-gray-300">
                  Thanks for trying this export. Your feedback helps shape what we build next.
                </p>
              </div>
            </div>
            <div className="mt-4 grid gap-2">
              <Button className={EXPORT_REVIEW_ACTION_BUTTON_CLASS} onClick={onOpenFeedback}>
                <MessageSquare className="h-4 w-4" />
                Send us your feedback
              </Button>
              <Button className={EXPORT_REVIEW_ACTION_BUTTON_CLASS} onClick={onOpenUpdates}>
                <Mail className="h-4 w-4" />
                Get feature updates
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="mb-3 flex items-start gap-3">
              <div
                className={`mt-0.5 rounded-md border p-2 shadow-[0_0_0_1px_rgba(255,255,255,0.08)] ${
                  isClean
                    ? "border-emerald-400/80 bg-emerald-500/10 text-emerald-300"
                    : "border-amber-400/80 bg-amber-500/10 text-amber-300"
                }`}
              >
                {isClean ? (
                  <ShieldCheck className="h-5 w-5" aria-hidden />
                ) : (
                  <AlertTriangle className="h-5 w-5" aria-hidden />
                )}
              </div>
              <div className="min-w-0">
                <h2
                  id="export-review-title"
                  className="flex items-center gap-1.5 text-base font-semibold text-gray-50"
                >
                  <span>
                    {exportName}: {title}
                  </span>
                  {helperText ? <InfoTooltip text={helperText} widthClass="w-72" /> : null}
                </h2>
                {!hideSummary && review.summary ? (
                  <p className="mt-1 text-sm text-gray-300">{review.summary}</p>
                ) : null}
              </div>
            </div>
            {typeof onProjectNameChange === "function" ? (
              <div>
                <label
                  htmlFor="export-project-name"
                  className="mb-1 block text-xs font-medium text-gray-300"
                >
                  Project name
                </label>
                <input
                  id="export-project-name"
                  type="text"
                  value={projectName}
                  onChange={(event) => onProjectNameChange(event.target.value)}
                  className="w-full rounded-md border border-gray-600 bg-black/20 px-2 py-1.5 text-sm text-gray-100 placeholder:text-gray-500 focus:border-gray-500 focus:outline-none"
                  placeholder="Untitled Scene"
                />
              </div>
            ) : null}
            {details.length > 0 ? (
              <div className="mt-3">
                <h3 className="mb-1 px-1 text-xs font-medium text-gray-300">
                  {isBlocked ? "Issues" : "Warnings"}
                </h3>
                <div className="max-h-44 space-y-1 overflow-y-auto rounded-md border border-gray-600/50 bg-black/20 p-2">
                  {details.map((detail, index) => (
                    <div
                      key={`${detail}-${index}`}
                      className="rounded border border-[rgba(95,95,95,0.4)] bg-[rgba(48,48,48,0.92)] px-2 py-1 text-xs text-neutral-200"
                    >
                      {detail}
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            {extraContent ? <div className="mt-3">{extraContent}</div> : null}

            <Button
              className={`mt-3 ${isBlocked ? "w-full gap-2 border border-amber-700/70 bg-transparent text-amber-200 hover:border-amber-600 hover:bg-amber-950/45 hover:text-amber-100" : EXPORT_REVIEW_ACTION_BUTTON_CLASS}`}
              onClick={onDownload}
            >
              <Download className="h-4 w-4" />
              {actionLabel}
            </Button>
            <div className="mt-4 space-y-2">
              <ExportCountsDisclosure counts={counts} defaultOpen={showCountsByDefault} />
              {onSecondaryDownload ? (
                secondaryLabel ? (
                  <ExportModalDisclosure title="Advanced options">
                    {secondaryTitle || secondaryDescription || secondaryMeta ? (
                      <div className="mb-2 px-1">
                        {secondaryTitle ? (
                          <div className="text-xs font-medium text-gray-200">{secondaryTitle}</div>
                        ) : null}
                        {secondaryDescription ? (
                          <p className="mt-1 text-xs leading-4 panel-text-helper">
                            {secondaryDescription}
                          </p>
                        ) : null}
                        {secondaryMeta ? (
                          <div className="mt-2 text-xs font-medium text-gray-300">
                            {secondaryMeta}
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                    <Button
                      className="w-full gap-2 border border-gray-700 bg-transparent text-xs text-gray-300 hover:border-gray-600 hover:bg-gray-800 hover:text-gray-100"
                      onClick={onSecondaryDownload}
                    >
                      <Package className="h-3.5 w-3.5" />
                      {secondaryLabel}
                    </Button>
                  </ExportModalDisclosure>
                ) : (
                  <p className="mt-2 text-center text-xs text-gray-400">
                    No assets referenced — single-file download only.
                  </p>
                )
              ) : null}
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
});

const KeyframeMismatchModal = React.memo(function KeyframeMismatchModal({
  pending,
  onSkip,
  onAbort,
}) {
  const dialogRef = React.useRef(null);
  useFocusTrap(dialogRef, Boolean(pending));
  if (!pending) return null;
  return createPortal(
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/60 p-4">
      <div
        ref={dialogRef}
        className="w-full max-w-md rounded-lg border border-gray-700 p-4 text-gray-100 shadow-xl backdrop-blur-sm"
        style={{ backgroundColor: "var(--panel-surface)" }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="keyframe-mismatch-title"
        tabIndex={-1}
      >
        <div className="mb-3 flex items-start gap-3">
          <div className="mt-0.5 rounded-md border border-amber-400/80 bg-amber-500/10 p-2 text-amber-300 shadow-[0_0_0_1px_rgba(255,255,255,0.08)]">
            <AlertTriangle className="h-5 w-5" aria-hidden />
          </div>
          <div className="min-w-0">
            <h2 id="keyframe-mismatch-title" className="text-base font-semibold text-gray-50">
              Keyframe DOF mismatch
            </h2>
            <p className="mt-1 text-sm text-gray-300">
              These keyframes do not match the current model DOF count. Skip them or abort export.
            </p>
          </div>
        </div>
        <div className="max-h-44 space-y-1 overflow-y-auto rounded-md border border-gray-600/50 bg-black/20 p-2">
          {pending.invalid.map((item) => (
            <div
              key={item.name}
              className="rounded border border-[rgba(95,95,95,0.4)] bg-[rgba(48,48,48,0.92)] px-2 py-1 text-xs text-neutral-200"
            >
              {item.name}: {item.actual} values, expected {item.expected}
            </div>
          ))}
        </div>
        <Button className="mt-4 w-full gap-2" onClick={onSkip}>
          Skip Invalid Keyframes
        </Button>
        <Button
          variant="danger"
          className="mt-2 w-full gap-2 border border-red-500/70 bg-red-600/90 text-white hover:border-red-400 hover:bg-red-500"
          onClick={onAbort}
        >
          Abort Export
        </Button>
      </div>
    </div>,
    document.body
  );
});

const PoseEditorPanelContainer = React.memo(function PoseEditorPanelContainer({
  open,
  onClose,
  titleIcon,
  onCreateKeyframe,
  onDeleteKeyframe,
  mobilePinPanelId,
}) {
  const keyframes = useFileStore((state) => state.keyframes);
  const selectedKeyframe = useSelectionStore((state) => state.selectedKeyframe);
  const fileActions = useFileStore.getState().actions;

  return (
    <PoseEditor
      open={open}
      onClose={onClose}
      titleIcon={titleIcon}
      keyframes={keyframes}
      handleKeyframeSelect={fileActions.handleKeyframeSelect}
      selectedKeyframe={selectedKeyframe}
      onCreateKeyframe={onCreateKeyframe}
      onDeleteKeyframe={onDeleteKeyframe}
      interpolateKeyframes={fileActions.interpolateKeyframes}
      mobilePinPanelId={mobilePinPanelId}
    />
  );
});

export {
  ExportReviewModal,
  FileUploadPanelContainer,
  KeyframeMismatchModal,
  PoseEditorPanelContainer,
};
